import { useState, useEffect } from 'react'
import { useParams, Link, useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Activity, 
  Users, 
  MessageSquare, 
  TrendingUp, 
  ArrowRight,
  Settings,
  Play,
  Link as LinkIcon,
  Clock,
  Inbox
} from 'lucide-react'
import { getFirebaseDb } from '@/lib/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { OrganizationContext } from './AssistPage'

interface SignalStats {
  totalSignals: number
  last24Hours: number
  last7Days: number
}

export function DashboardPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const orgContext = useOutletContext<OrganizationContext>()
  const [stats, setStats] = useState<SignalStats | null>(null)
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    // Get organization ID from outlet context (passed from AssistPage)
    const organizationId = orgContext?.organizationId
    if (!organizationId) {
      setLoading(false)
      return
    }
    
    let unsubscribe: (() => void) | undefined
    
    const setupListener = async () => {
      try {
        const db = getFirebaseDb()
        
        // Set up real-time listener for statistics using org ID from context
        const statsRef = doc(db, 'organizations', organizationId, 'statistics', 'totals')
        unsubscribe = onSnapshot(statsRef, (statsDoc) => {
          if (statsDoc.exists()) {
            const statsData = statsDoc.data()
            setStats({
              totalSignals: statsData.totalSignals || 0,
              last24Hours: statsData.last24Hours || 0,
              last7Days: statsData.last7Days || 0
            })
          } else {
            setStats({
              totalSignals: 0,
              last24Hours: 0,
              last7Days: 0
            })
          }
          setLoading(false)
        }, (error) => {
          console.error('Error listening to stats:', error)
          setLoading(false)
        })
      } catch (error) {
        console.error('Error setting up stats listener:', error)
        setLoading(false)
      }
    }
    
    setupListener()
    
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [orgContext?.organizationId])
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome to the Session Customer Assistant platform
        </p>
      </div>

      {/* Metrics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signals</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? '...' : stats?.totalSignals.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats && stats.last24Hours > 0 
                ? `+${stats.last24Hours.toLocaleString()} in last 24h`
                : 'All time total'
              }
            </p>
          </CardContent>
        </Card>
        
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground/50" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground/50">—</div>
            <Badge variant="secondary" className="text-xs mt-1">
              <Clock className="h-3 w-3 mr-1" />
              Coming Soon
            </Badge>
          </CardContent>
        </Card>
        
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversations</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground/50" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground/50">—</div>
            <Badge variant="secondary" className="text-xs mt-1">
              <Clock className="h-3 w-3 mr-1" />
              Coming Soon
            </Badge>
          </CardContent>
        </Card>
        
        <Card className="relative">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground/50" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground/50">—</div>
            <Badge variant="secondary" className="text-xs mt-1">
              <Clock className="h-3 w-3 mr-1" />
              Coming Soon
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Inbox className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-lg mb-1">No activity yet</h3>
              <p className="text-sm text-muted-foreground max-w-[250px]">
                Activity will appear here once you start sending signals or interacting with the platform.
              </p>
              <Link 
                to={`/${orgId}/assist/signals/code`}
                className="text-sm text-primary hover:underline mt-4"
              >
                Get started with integration →
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              <Link 
                to={`/${orgId}/assist/signals`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Activity className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">View Signals</div>
                    <div className="text-sm text-muted-foreground">
                      Monitor event tracking data
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              
              <Link 
                to={`/${orgId}/assist/settings`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Settings className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">Configure Settings</div>
                    <div className="text-sm text-muted-foreground">
                      Update platform configuration
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              
              <Link 
                to={`/${orgId}/assist/signals/code`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Play className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">Integration Code</div>
                    <div className="text-sm text-muted-foreground">
                      Get code snippets for your app
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
              
              <Link 
                to={`/${orgId}/assist/settings?tab=api-keys`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-medium">API Keys</div>
                    <div className="text-sm text-muted-foreground">
                      Manage API access tokens
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle>System Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div>
                <div className="font-medium">Signals API</div>
                <div className="text-sm text-muted-foreground">Operational</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div>
                <div className="font-medium">Database</div>
                <div className="text-sm text-muted-foreground">Healthy</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div>
                <div className="font-medium">Authentication</div>
                <div className="text-sm text-muted-foreground">All systems go</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
